import { Scene } from '../../../../types/scene';
import { CompositorRecordingData } from '../types/manifest';

const DEFAULT_BACKGROUND = { type: 'color' as const, value: '#1a1a2e' };

/**
 * Convert layout.background to standard format for compositor/FFmpeg
 * layout.background is the SINGLE SOURCE OF TRUTH - captured at recording time
 */
function formatBackground(bg: any) {
  if (!bg) return DEFAULT_BACKGROUND;
  
  // Handle image backgrounds
  if (bg.type === 'image') {
    const url = bg.imageUrl || bg.imageKey || bg.value || bg.key;
    const imageKey = bg.imageKey || bg.key;
    
    if (url) {
      return { type: 'image' as const, imageUrl: url, imageKey: imageKey };
    }
    return DEFAULT_BACKGROUND;
  }
  
  // Handle solid color (type: 'solid' with colors array)
  if (bg.type === 'solid' && bg.colors?.[0]) {
    return { type: 'color' as const, value: bg.colors[0] };
  }
  
  // Handle gradient
  if (bg.type === 'gradient') {
    const value = bg.value || (bg.colors ? `linear-gradient(135deg, ${bg.colors.join(', ')})` : null);
    if (value) return { type: 'gradient' as const, value };
    return DEFAULT_BACKGROUND;
  }
  
  // Handle color (type: 'color' with value)
  if (bg.type === 'color' && bg.value) {
    return { type: 'color' as const, value: bg.value };
  }
  
  return DEFAULT_BACKGROUND;
}

export function buildRecordingDataFromScene(scene: Scene): CompositorRecordingData | null {
  try {
    const sceneWithMeta = scene as any;
    if (!sceneWithMeta.metadata) return null;
    
    const metadata = typeof sceneWithMeta.metadata === 'string' 
      ? JSON.parse(sceneWithMeta.metadata) 
      : sceneWithMeta.metadata;
    
    if (metadata.recordingType !== 'separate_streams') return null;

    // SINGLE SOURCE OF TRUTH: layout.background (captured at recording time)
    const background = formatBackground(metadata.layout?.background);
    
    const recordingData: CompositorRecordingData = {
      duration: scene.duration || metadata.duration || 0,
      layout: {
        canvas: metadata.layout?.canvas || { width: 1920, height: 1080, aspectRatio: '16:9' },
        webcam: metadata.layout?.webcam,
        screen: metadata.layout?.screen,
        background,
      },
      streams: {
        webcam: metadata.streams?.webcam,
        screen: metadata.streams?.screen,
        audio: metadata.streams?.audio,
      },
      version: metadata.version,
    };

    return recordingData;
  } catch (err) {
    console.error('[sceneToRecordingData] Failed to build recording data from scene:', err);
    return null;
  }
}

export function isSeparateStreamsScene(scene: Scene): boolean {
  try {
    const sceneWithMeta = scene as any;
    if (!sceneWithMeta.metadata) return false;
    
    const metadata = typeof sceneWithMeta.metadata === 'string' 
      ? JSON.parse(sceneWithMeta.metadata) 
      : sceneWithMeta.metadata;
    
    return metadata.recordingType === 'separate_streams';
  } catch {
    return false;
  }
}

export function getSceneTransitionOut(scene: Scene): { type: string; duration: number } {
  try {
    const sceneWithMeta = scene as any;
    if (!sceneWithMeta.metadata) return { type: 'cut', duration: 0 };
    
    const metadata = typeof sceneWithMeta.metadata === 'string' 
      ? JSON.parse(sceneWithMeta.metadata) 
      : sceneWithMeta.metadata;
    
    if (metadata.transitionOut) {
      return {
        type: metadata.transitionOut.type || 'cut',
        duration: metadata.transitionOut.type === 'cut' ? 0 : (metadata.transitionOut.duration || 1.0),
      };
    }
    
    return { type: 'cut', duration: 0 };
  } catch {
    return { type: 'cut', duration: 0 };
  }
}
